import { setExamStatsTab, setSelectedApplicantsIds } from '../../../../../store/workspace/slice';
import { getExaminersThunk, updateExaminersStatusThunk } from '../../../../../store/workspace/thunks';
import { AshbyLogoIcon, DownloadIcon, NoteIcon, PrintIcon, RefreshIcon } from '../../../../../assets/icons';
import { ExamStatsTab } from '../../../../../store/workspace/types';
import { examStatsDefaultActions, interviewActions } from '../../../../../configs';
import { selectUserId } from '../../../../../store/auth/selectors';
import { useDispatch, useSelector } from '../../../../../store/store';
import { ListItem } from '../../../../../store/general/types';
import TableSkeleton from '../../../../../components/TableSkeleton';
import Pagination from '../../../../../components/Pagination';
import DropDown from '../../../../../components/DropDown';
import Button from '../../../../../components/Button';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import RecruitingInterviewTable from './tables/RecruitingInterviewTable';
import MultipleChoiceExamTable from './tables/MultipleChoiceExamTable';
import OverallResultsTable from './tables/OverallResultsTable';
import FinalResultTable from './tables/FinalResultTable';
import CodingExamTable from './tables/CodingExamTable';
import InterviewTable from './tables/InterviewTable';
import ExamTabs, { buildExamTabs } from '../../components/ExamTabs';
import { EXAM_STATS_TABS, JOBS_PAGE_SIZES, TAB_SKELETON_COLUMNS } from '../../constants';
import styles from './style.module.scss';
import {
    selectExamStatsTab,
    selectCodingExamStats,
    selectExamStatsJobInfo,
    selectFinalResultStats,
    selectSelectedApplicantsIds,
    selectPersonalInterviewStats,
    selectMultipleChoiceExamStats,
    selectOverallResultStatists,
    selectFetchingExaminersDetail,
    selectRecruitingInterviewStatists,
} from '../../../../../store/workspace/selectors';
import {
    exportCodingExamTableToExcel,
    exportFinalResultTableToExcel,
    exportPersonalInterviewTableToExcel,
    exportMultipleChoiceExamTableToExcel,
    exportRecruitingInterviewTableToExcel,
} from '../../../../../utils/exportHelpers';

const DEFAULT_PAGE_SIZE = JOBS_PAGE_SIZES[0];

function ExaminersStatics() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { jobId } = useParams();
    const userId = useSelector(selectUserId);
    const tableRef = useRef<HTMLDivElement>(null);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
    const [action, setAction] = useState<ListItem | null>(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);

    const tab = useSelector(selectExamStatsTab);
    const jobInfo = useSelector(selectExamStatsJobInfo);
    const fetching = useSelector(selectFetchingExaminersDetail);
    const selectedApplicantsIds = useSelector(selectSelectedApplicantsIds);

    const overallResult = useSelector(selectOverallResultStatists);
    const recruitingInterview = useSelector(selectRecruitingInterviewStatists);
    const multipleChoiceExam = useSelector(selectMultipleChoiceExamStats);
    const codingExam = useSelector(selectCodingExamStats);
    const interview = useSelector(selectPersonalInterviewStats);
    const finalResult = useSelector(selectFinalResultStats);

    const fetchTablesData = useCallback((
        nextTab: ExamStatsTab,
        nextPage = 1,
        nextLimit = limit,
    ) => {
        if (!jobId) return;
        dispatch(getExaminersThunk({ jobId, tab: nextTab, page: nextPage, limit: nextLimit }));
    }, [dispatch, jobId, limit]);

    useEffect(() => {
        fetchTablesData(tab);
        // Only on mount: the API answers with whichever tab it considers default.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updatePage = (nextPage: number) => {
        setPage(nextPage);
        fetchTablesData(tab, nextPage);
    };

    const updateLimit = (nextLimit: number) => {
        setLimit(nextLimit);
        setPage(1);
        fetchTablesData(tab, 1, nextLimit);
    };

    const refreshTable = () => {
        setPage(1);
        fetchTablesData(tab);
    };

    const changeTab = (nextTab: ExamStatsTab) => {
        fetchTablesData(nextTab);
        dispatch(setSelectedApplicantsIds([]));
        dispatch(setExamStatsTab(nextTab));
        setPage(1);
    };

    const downloadTable = () => {
        switch (tab) {
            case 'coding':
                return exportCodingExamTableToExcel(codingExam?.examiners || []);
            case 'multipleChoice':
                return exportMultipleChoiceExamTableToExcel(multipleChoiceExam?.examiners || []);
            case 'recruitingInterview':
                return exportRecruitingInterviewTableToExcel(recruitingInterview?.examiners || []);
            case 'interview':
                return exportPersonalInterviewTableToExcel(interview?.examiners || []);
            case 'finalResult':
                return exportFinalResultTableToExcel(finalResult?.examiners || []);
            default:
                return;
        }
    };

    const updateExaminersStatus = async () => {
        if (!jobId) return;

        try {
            setUpdatingStatus(true);
            await dispatch(updateExaminersStatusThunk({
                jobId,
                userId,
                status: action?.id,
                examinerIds: selectedApplicantsIds,
            }));

            dispatch(getExaminersThunk({ jobId, userId }));
            dispatch(setSelectedApplicantsIds([]));
            setAction(null);
        } catch (e: unknown) {
            console.warn(e);
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handlePrint = useReactToPrint({
        contentRef: tableRef,
        documentTitle: 'Applicants list',
    });

    /**
     * Bulk actions are only offered while the applicants of the current tab are
     * the last graded step of the pipeline — otherwise a later exam decides.
     */
    const actionList = useMemo(() => {
        switch (tab) {
            case 'recruitingInterview':
                return jobInfo?.recruitingInterview === 'enable' &&
                    jobInfo.multipleChoiceExam === 'disable' &&
                    jobInfo.codingExam === 'disable'
                    ? examStatsDefaultActions : undefined;
            case 'multipleChoice':
                return jobInfo?.recruitingInterview !== 'pending' &&
                    jobInfo?.multipleChoiceExam === 'enable' &&
                    jobInfo.codingExam === 'disable'
                    ? examStatsDefaultActions : undefined;
            case 'coding':
                return jobInfo?.codingExam === 'enable' ? examStatsDefaultActions : undefined;
            case 'interview':
                return interviewActions;
            default:
                return undefined;
        }
    }, [tab, jobInfo]);

    const tabs = useMemo(() => buildExamTabs(jobInfo, EXAM_STATS_TABS), [jobInfo]);

    /**
     * `Pagination` derives the page count itself, so it expects the total number
     * of records rather than the number of pages.
     */
    const totalRecords = useMemo(() => {
        switch (tab) {
            case 'overall':
                return overallResult?.total || 0;
            case 'recruitingInterview':
                return recruitingInterview?.total || 0;
            case 'multipleChoice':
                return multipleChoiceExam?.total || 0;
            case 'coding':
                return codingExam?.total || 0;
            case 'interview':
                return interview?.total || 0;
            case 'finalResult':
                return finalResult?.total || 0;
            default:
                return 0;
        }
    }, [tab, overallResult, recruitingInterview, multipleChoiceExam, codingExam, interview, finalResult]);

    const selectable = Boolean(actionList);

    const renderTable = () => {
        if (fetching) {
            return <TableSkeleton
                rows={8}
                selectable={selectable}
                grouped={tab === 'multipleChoice' || tab === 'coding'}
                columns={tab ? TAB_SKELETON_COLUMNS[tab] : TAB_SKELETON_COLUMNS.overall}
            />;
        }

        switch (tab) {
            case 'overall':
                return <OverallResultsTable ref={tableRef} jobId={jobId} staticsData={overallResult} />;
            case 'recruitingInterview':
                return <RecruitingInterviewTable
                    ref={tableRef}
                    jobId={jobId}
                    selectable={selectable}
                    statics={recruitingInterview}
                />;
            case 'multipleChoice':
                return <MultipleChoiceExamTable
                    ref={tableRef}
                    jobId={jobId}
                    selectable={selectable}
                    examinersInfo={multipleChoiceExam?.examiners || []}
                />;
            case 'coding':
                return <CodingExamTable
                    ref={tableRef}
                    jobId={jobId}
                    selectable={selectable}
                    examinersInfo={codingExam?.examiners || []}
                />;
            case 'interview':
                return <InterviewTable ref={tableRef} jobId={jobId} examinersData={interview?.examiners || []} />;
            case 'finalResult':
                return <FinalResultTable
                    ref={tableRef}
                    jobId={jobId}
                    examinersData={finalResult?.examiners || []}
                    fetchTablesData={fetchTablesData}
                />;
            default:
                return null;
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.main}>
                <div className={styles.main_header}>
                    <span className={styles.main_header_title}>
                        <span>{jobInfo?.title}</span>
                        {jobInfo?.platform === 'Ashby' && <AshbyLogoIcon width={71} height={23} />}
                    </span>
                    {tab !== 'interview' && tab !== 'finalResult' && <Button
                        type='Primary'
                        text='Exam sheet'
                        icon={<NoteIcon />}
                        iconPosition='Left'
                        onClick={() => navigate('sheet')}
                    />}
                </div>

                <ExamTabs tabs={tabs} activeTab={tab} onChange={changeTab} />

                <div className={styles.main_table}>
                    <div className={styles.main_table_header}>
                        <div className={styles.main_table_header_title}>
                            <span>Applicants list</span>
                            <div className={styles.main_table_header_title_actions}>
                                {JOBS_PAGE_SIZES.map(size => <button
                                    key={size}
                                    data-is-active={limit === size}
                                    onClick={() => updateLimit(size)}
                                    className={styles.main_table_header_title_actions_item}
                                >
                                    <span>{size}</span>
                                </button>)}
                            </div>
                        </div>
                        <div className={styles.main_table_header_actions}>
                            <Button
                                type='Light'
                                text='Download'
                                iconPosition='Right'
                                icon={<DownloadIcon />}
                                onClick={downloadTable}
                                className={styles.main_table_header_actions_btn}
                            />
                            <Button
                                type='Light'
                                text='Print list'
                                iconPosition='Right'
                                icon={<PrintIcon />}
                                onClick={handlePrint}
                                className={styles.main_table_header_actions_btn}
                            />
                            <Button
                                type='Light'
                                text='Refresh list'
                                iconPosition='Right'
                                icon={<RefreshIcon />}
                                onClick={refreshTable}
                                className={styles.main_table_header_actions_btn}
                            />
                        </div>
                    </div>

                    {renderTable()}

                    {totalRecords > 1 && <Pagination
                        page={page}
                        limit={limit}
                        total={totalRecords}
                        setPage={updatePage}
                    />}
                </div>

                {actionList && <div className={styles.main_action}>
                    <span className={styles.main_action_count}>
                        {selectedApplicantsIds.length} {selectedApplicantsIds.length > 1 ? 'Accounts' : 'Account'} Selected
                    </span>
                    <DropDown
                        idKey='id'
                        openListOnTop
                        titleKey='title'
                        setItem={setAction}
                        selectedItem={action}
                        dropDownList={actionList}
                        placeholder='Select an action'
                        containerClassName={styles.main_action_dropdown}
                        wrapperClassName={styles.main_action_dropdown_wrapper}
                    />
                    <Button
                        type='Primary'
                        text='Submit'
                        loading={updatingStatus}
                        onClick={updateExaminersStatus}
                        disabled={selectedApplicantsIds.length === 0 || !action}
                    />
                </div>}
            </div>
        </div>
    );
};

export default ExaminersStatics;
